import React, { useState, useEffect, useRef } from "react";
import "./styles.css";
import {
  fetchSpeech,
  getGeneratedPDF,
  fetchGenkitSpeech,
} from "../../api/chat";
import {
  useAudioController,
  setupGlobalAbortErrorHandler,
} from "../../hooks/useAudioController";
import { STAR_ICON } from "../../assets/Images";
import MarkdownIt from "markdown-it";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import Tooltip from "@mui/material/Tooltip";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import {
  moderateScale,
  parseColor,
  processMessageContent,
  getCustomBackgroundColor,
} from "../../util/utils";
import { BsRobot } from "react-icons/bs";
import { BiSolidUser } from "react-icons/bi";
import { BsRepeat } from "react-icons/bs";
import { BiStopCircle } from "react-icons/bi";
import { LuCopy, LuFileText, LuDownload } from "react-icons/lu";
import DOMPurify from "dompurify";
import CircularProgress from "@mui/material/CircularProgress";

function ChatMessage({
  message,
  speechConfigData,
  isSpeaking,
  index,
  setSpeakingStates,
  isReSpeaking,
  setIsReSpeaking,
  isMicOn,
  appearance,
  getVariationImage,
}) {
  const validImageTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
    "image/gif",
  ];
  const isScreenMdOrLarger = window.innerWidth >= 768;
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Use centralized audio controller
  const { executeAudioOperation, stopAllAudio } = useAudioController();

  // Setup global error handler
  useEffect(() => {
    const cleanup = setupGlobalAbortErrorHandler();
    return cleanup;
  }, []);

  const handleFetchSpeech = async (message) => {
    try {
      if (!isMicOn) return;

      setIsReSpeaking({});
      setSpeakingStates({});
      setIsReSpeaking((prev) => ({ ...prev, [index]: true }));

      // Process message content to remove hidden color codes for speech
      const { speechText } = processMessageContent(message.content);

      // Determine speech gateway
      const speechGateway = speechConfigData?.gateway || "openai";
      const isGenkitSpeech = speechGateway === "genkit";

      await executeAudioOperation(
        // Audio operation function
        (signal) =>
          isGenkitSpeech
            ? fetchGenkitSpeech(
                { text: speechText, ...speechConfigData },
                signal,
              )
            : fetchSpeech({ text: speechText, ...speechConfigData }, signal),
        // Success callback
        () => {
          // Audio completed successfully
        },
        // Error callback
        (error) => {
          // Only log non-abort errors
          if (
            error.name !== "AbortError" &&
            !error.message?.includes("abort")
          ) {
            console.error("Error during speech synthesis:", error);
          }
        },
      );
    } catch (error) {
      // Catch any errors that slip through
      if (error.name !== "AbortError" && !error.message?.includes("abort")) {
        console.error("Unexpected error in handleFetchSpeech:", error);
      }
    } finally {
      // Always clean up state, even if there's an error
      try {
        setIsReSpeaking((prev) => ({ ...prev, [index]: false }));
      } catch (error) {
        // Even state setters can fail
      }
    }
  };
  const handleStopFetchingSpeech = () => {
    try {
      stopAllAudio();
      setIsReSpeaking({});
      setSpeakingStates({});
    } catch (error) {
      // Silently handle any stop errors
      if (error.name !== "AbortError" && !error.message?.includes("abort")) {
        console.error("Error stopping speech:", error);
      }
    }
  };
  // Process message content to extract color codes and clean text
  const { displayText, backgroundColor } = processMessageContent(
    message.content,
  );
  let messageString = displayText;

  // Unescape HTML entities if the content is escaped HTML
  const unescapeHtml = (str) => {
    const div = document.createElement("div");
    div.innerHTML = str;
    return div.textContent || div.innerText || "";
  };

  // Check if content looks like escaped HTML
  const isEscapedHTML = (str) => {
    return str.includes("&lt;") && str.includes("&gt;");
  };

  // If content is escaped HTML, unescape it
  if (isEscapedHTML(messageString)) {
    messageString = unescapeHtml(messageString);
  }

  let lines = !(message.responseType === "image") && messageString.split("\n");

  const isHTML = (str) => {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(str);
  };

  const isContract = (htmlString) => {
    const contractTitleRegex = /<title>\s*Contract\s*<\/title>/i;
    return contractTitleRegex.test(htmlString);
  };

  const detectContract = (str) => {
    if (message.responseType === "image") {
      return false;
    }
    if (!str.trim()) {
      return { type: "empty", message: "Empty string" };
    }

    if (isHTML(str)) {
      if (isContract(str)) {
        return true;
      }
    }
    return false;
  };

  const htmlContentRef = useRef("");

  const fixMalformedTags = (html) => {
    let result = html;

    result = result.replace(/< *tdstyle/gi, "<td style");
    result = result.replace(/<\/ *tdstyle/gi, "</td");

    result = result.replace(
      /border *: *1 *px *solid *#/g,
      "border:1px solid #",
    );
    result = result.replace(/padding *: *6 *px *12 *px/g, "padding:6px 12px");
    result = result.replace(/padding *: *6 *px12 *px/g, "padding:6px 12px");
    result = result.replace(/text *-align/g, "text-align");

    result = result.replace(/: *(\d+) *px/g, ":$1px");
    result = result.replace(/# *([a-f0-9]{6})/gi, "#$1");
    result = result.replace(/; *padding/g, ";padding");
    result = result.replace(/; *border/g, ";border");
    result = result.replace(/; *text/g, ";text");

    result = result.replace(/&lt;/g, "<");
    result = result.replace(/&gt;/g, ">");
    result = result.replace(/&amp;/g, "&");

    return result;
  };

  const fixCssUnits = (html) => {
    let result = html;

    result = result.replace(/(\d+)\s*px/g, "$1px");
    result = result.replace(/(\d+)\s*em/g, "$1em");
    result = result.replace(/(\d+)\s*rem/g, "$1rem");
    result = result.replace(/(\d+)\s*%/g, "$1%");
    result = result.replace(/#\s*([a-f0-9]{6})/gi, "#$1");
    result = result.replace(/#\s*([a-f0-9]{3})/gi, "#$1");

    return result;
  };

  const cleanContractHtml = (input) => {
    let result = input;

    result = fixCssUnits(result);

    result = fixMalformedTags(result);

    result = DOMPurify.sanitize(result, {
      ALLOWED_TAGS: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "div",
        "p",
        "table",
        "thead",
        "tbody",
        "tr",
        "td",
        "th",
        "ul",
        "ol",
        "li",
        "strong",
        "em",
        "br",
        "svg",
        "rect",
        "text",
        "span",
        "head",
        "title",
        "style",
        "html",
        "body",
        "footer",
        "header",
        "section",
      ],
      ALLOWED_ATTR: [
        "style",
        "class",
        "width",
        "height",
        "viewBox",
        "xmlns",
        "x",
        "y",
        "fill",
        "font-size",
        "cellpadding",
        "cellspacing",
        "lang",
      ],
    });
    htmlContentRef.current = result;

    return result;
  };

  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
    highlight(str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return `<pre class="hljs"><code>${
            hljs.highlight(str, { language: lang, ignoreIllegals: true }).value
          }</code></pre>`;
        } catch (__) {
          // eslint-disable-next-line no-unused-vars
        }
      }
      return `<pre class="hljs"><code>${md.utils.escapeHtml(str)}</code></pre>`;
    },
  });

  const isContractMsg = detectContract(messageString);

  const cleanHtml =
    message.responseType === "image"
      ? ""
      : isContractMsg
        ? messageString.replace(/```html|```/g, "").trim()
        : md.render(messageString);

  const copyToClipboard = () => {
    // Use clean text without hidden color codes for copying
    navigator.clipboard.writeText(displayText).then(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 1000);
    });
  };

  const downloadAsPDF = async () => {
    try {
      setIsDownloading(true);
      const html = displayText;
      const data = {
        html,
        options: {
          format: "A4",
          landscape: false,
          margin: {
            top: "2cm",
            right: "2cm",
            bottom: "2cm",
            left: "2cm",
          },
        },
      };

      const response = await getGeneratedPDF(data);

      // Convert object with numeric keys to Uint8Array
      const keys = Object.keys(response).sort(
        (a, b) => parseInt(a, 10) - parseInt(b, 10),
      );
      const byteArray = new Uint8Array(keys.length);

      // Fill the array with bytes from the object
      keys.forEach((key, keyIndex) => {
        byteArray[keyIndex] = response[key];
      });

      // Create blob and download
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "test.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownload = (url) => {
    // For data URLs (base64) and external URLs, using a temporary link
    // with the 'download' attribute is more robust for triggering a download
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // Determine a filename
    let filename = "downloaded-image";
    if (url.startsWith("data:")) {
      // Extract extension from mime type
      const mimeType = url.split(";")[0].split(":")[1];
      const extension = mimeType.split("/")[1] || "png";
      filename = `generated-image-${Date.now()}.${extension}`;
    } else {
      // Extract filename from URL if possible
      try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname.split("/");
        const lastSegment = pathSegments[pathSegments.length - 1];
        if (lastSegment && lastSegment.includes(".")) {
          filename = lastSegment;
        } else {
          filename = `image-${Date.now()}.png`;
        }
      } catch (e) {
        filename = `image-${Date.now()}.png`;
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getGridClass = (contentLength) => {
    if (contentLength === 1) return "grid grid-cols-1 gap-[10px]";
    if (contentLength === 2)
      return "grid grid-cols-1 lg:grid-cols-2 gap-[10px]";
    return "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-[10px]";
  };

  return (
    <div className="mb-[20px]">
      {message.role === "assistant" ? (
        <div className="flex justify-start items-center">
          <div className="flex">
            <div
              className="flex shrink-0 justify-center items-center shadow-xl mr-[10px]"
              style={{
                borderWidth: "2px",
                borderColor:
                  appearance?.assistantChatBubbleBackgroundColor || "#2196F3",
                borderStyle: "solid",

                height: isScreenMdOrLarger
                  ? moderateScale(42, appearance?.chatFontSize || 1)
                  : moderateScale(30, appearance?.chatFontSize || 1),
                width: isScreenMdOrLarger
                  ? moderateScale(42, appearance?.chatFontSize || 1)
                  : moderateScale(30, appearance?.chatFontSize || 1),
                borderRadius: isScreenMdOrLarger
                  ? moderateScale(21, appearance?.chatFontSize || 1)
                  : moderateScale(15, appearance?.chatFontSize || 1),
                padding: moderateScale(5, appearance?.chatFontSize || 1),
              }}
            >
              <BsRobot
                color={
                  appearance?.assistantChatBubbleBackgroundColor || "#2196F3"
                }
                className="flex w-full h-full"
              />
            </div>
            <div
              style={{
                background:
                  message.type && message.type === "error"
                    ? `rgba(${parseColor("#e34e3d")}, ${String(
                        (appearance?.assistantChatBubbleBackgroundOpacity ||
                          100) / 100,
                      )})`
                    : getCustomBackgroundColor(
                        appearance?.assistantChatBubbleBackgroundColor ||
                          "#2196F3",
                        backgroundColor,
                        appearance?.assistantChatBubbleBackgroundOpacity || 100,
                      ),
                fontSize: isScreenMdOrLarger
                  ? moderateScale(16, appearance?.chatFontSize || 1)
                  : moderateScale(16, appearance?.chatFontSize || 1),
              }}
              className={`flex flex-col p-[10px] font-poppins font-[400] rounded-r-1x rounded-bl-1x shadow-assist mr-[12px] md:mr-[15px]`}
            >
              {message.responseType === "image" ? (
                <div className={getGridClass(message?.content.length)}>
                  {message?.content?.map((msg, index) => (
                    <div
                      key={index}
                      className="relative flex flex-col"
                      style={{ minHeight: "128px", minWidth: "128px" }}
                    >
                      <div
                        className="absolute top-2 right-2 mt-[5px] mr-[5px] cursor-pointer z-10 bg-stone-200 p-[5px] rounded-md"
                        onClick={() => handleDownload(msg.url)}
                      >
                        <Tooltip title={"Download"}>
                          <LuDownload
                            color={
                              appearance?.downloadButtonIconColor || "#000000"
                            }
                            size={
                              isScreenMdOrLarger
                                ? moderateScale(25, 1)
                                : moderateScale(20, 1)
                            }
                          />
                        </Tooltip>
                      </div>
                      <div
                        className="absolute top-2 left-2 mt-[5px] mr-[5px] cursor-pointer z-10 bg-stone-200 p-[5px] rounded-md"
                        onClick={() => {
                          getVariationImage(msg.url);
                        }}
                      >
                        <Tooltip title={"Generate Variation"}>
                          <STAR_ICON className="md:w-[25px] md:h-[25px] w-[20px] h-[20px]" />
                        </Tooltip>
                      </div>
                      <img
                        className="rounded-1x max-h-96"
                        key={index}
                        src={msg.url}
                        alt={msg.revised_prompt}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="markdown-content"
                  style={{
                    color: appearance?.assistantChatTextColor || "#FCFCFC",
                  }}
                  dangerouslySetInnerHTML={{
                    // __html: DOMPurify.sanitize(md.render(messageString)),
                    // __html: cleanContractHtml(cleanHtml),
                    __html: DOMPurify.sanitize(cleanContractHtml(cleanHtml)),
                  }}
                />
              )}
              {message.responseType !== "image" &&
                detectContract(messageString) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      borderTopWidth: 1,
                      borderTopColor: "divider",
                      borderTopStyle: "solid",
                      pt: 1,
                      mt: 0.5,
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton
                      onClick={downloadAsPDF}
                      aria-label="delete"
                      size="large"
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <CircularProgress
                          size={
                            isScreenMdOrLarger
                              ? moderateScale(25, 1)
                              : moderateScale(20, 1)
                          }
                        />
                      ) : (
                        <LuDownload
                          color={
                            appearance?.downloadButtonIconColor || "#000000"
                          }
                          size={
                            isScreenMdOrLarger
                              ? moderateScale(25, 1)
                              : moderateScale(20, 1)
                          }
                        />
                      )}
                    </IconButton>
                  </Box>
                )}
            </div>
          </div>

          {message.responseType !== "image" && (
            <div
              onClick={() => {
                (isReSpeaking || isSpeaking) && isHovered
                  ? handleStopFetchingSpeech()
                  : handleFetchSpeech(message);
              }}
              className={`shrink-0 ${
                !(isReSpeaking || isSpeaking) && "cursor-pointer"
              }`}
              style={{
                width: moderateScale(30, appearance?.chatFontSize || 1),
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {message.typing === true ? (
                <div
                  className="container"
                  style={{
                    "--uib-color": appearance?.chatBubbleIconColor || "#000000",
                  }}
                >
                  <div className="dot" />
                </div>
              ) : isReSpeaking || isSpeaking ? (
                isHovered ? (
                  <BiStopCircle
                    color={appearance?.chatBubbleIconColor || "#000000"}
                    size={
                      isScreenMdOrLarger
                        ? moderateScale(25, appearance?.chatFontSize || 1)
                        : moderateScale(20, appearance?.chatFontSize || 1)
                    }
                  />
                ) : (
                  <svg
                    width={
                      isScreenMdOrLarger
                        ? moderateScale(30, appearance?.chatFontSize || 1)
                        : moderateScale(25, appearance?.chatFontSize || 1)
                    }
                    height={
                      isScreenMdOrLarger
                        ? moderateScale(30, appearance?.chatFontSize || 1)
                        : moderateScale(25, appearance?.chatFontSize || 1)
                    }
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                    fill={appearance?.chatBubbleIconColor || "#000"}
                  >
                    <rect x="10" y="40" width="8" height="20" rx="2">
                      <animate
                        attributeName="height"
                        values="20;60;20"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="40;20;40"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </rect>
                    <rect x="20" y="30" width="8" height="40" rx="2">
                      <animate
                        attributeName="height"
                        values="40;80;40"
                        dur="1s"
                        begin="0.1s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="30;10;30"
                        dur="1s"
                        begin="0.1s"
                        repeatCount="indefinite"
                      />
                    </rect>
                    <rect x="30" y="20" width="8" height="60" rx="2">
                      <animate
                        attributeName="height"
                        values="60;90;60"
                        dur="1s"
                        begin="0.2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="20;5;20"
                        dur="1s"
                        begin="0.2s"
                        repeatCount="indefinite"
                      />
                    </rect>
                    <rect x="40" y="25" width="8" height="50" rx="2">
                      <animate
                        attributeName="height"
                        values="50;70;50"
                        dur="1s"
                        begin="0.3s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="25;15;25"
                        dur="1s"
                        begin="0.3s"
                        repeatCount="indefinite"
                      />
                    </rect>
                    <rect x="50" y="35" width="8" height="30" rx="2">
                      <animate
                        attributeName="height"
                        values="30;50;30"
                        dur="1s"
                        begin="0.4s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="35;25;35"
                        dur="1s"
                        begin="0.4s"
                        repeatCount="indefinite"
                      />
                    </rect>
                    <rect x="60" y="30" width="8" height="40" rx="2">
                      <animate
                        attributeName="height"
                        values="40;70;40"
                        dur="1s"
                        begin="0.5s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="y"
                        values="30;15;30"
                        dur="1s"
                        begin="0.5s"
                        repeatCount="indefinite"
                      />
                    </rect>
                  </svg>
                )
              ) : (
                <BsRepeat
                  color={appearance?.chatBubbleIconColor || "#000000"}
                  size={
                    isScreenMdOrLarger
                      ? moderateScale(25, appearance?.chatFontSize || 1)
                      : moderateScale(20, appearance?.chatFontSize || 1)
                  }
                  className="rotate-90"
                />
              )}
            </div>
          )}
          {!message.typing && message.responseType !== "image" && (
            <Tooltip
              title="Copied"
              open={showTooltip}
              disableHoverListener
              disableFocusListener
              disableTouchListener
            >
              <div
                className="ml-[5px] cursor-pointer"
                onClick={copyToClipboard}
              >
                <LuCopy
                  color={appearance?.chatBubbleIconColor || "#000000"}
                  size={
                    isScreenMdOrLarger
                      ? moderateScale(25, appearance?.chatFontSize || 1)
                      : moderateScale(20, appearance?.chatFontSize || 1)
                  }
                />
              </div>
            </Tooltip>
          )}
        </div>
      ) : (
        <div className="flex justify-end">
          <div className="flex">
            <div
              style={{
                background: `rgba(${parseColor(
                  appearance?.userChatBubbleBackgroundColor || "#26A69A",
                )}, ${String(
                  (appearance?.userChatBubbleBackgroundOpacity || 100) / 100,
                )})`,
                color: appearance?.userChatTextColor || "#FCFCFC",
                fontSize: isScreenMdOrLarger
                  ? moderateScale(16, appearance?.chatFontSize || 1)
                  : moderateScale(16, appearance?.chatFontSize || 1),
              }}
              className={`flex flex-col items-end justify-center px-[10px] py-[10px] font-poppins font-[400] rounded-l-1x rounded-br-1x shadow-user ml-[40px] md:ml-[50px] ${
                message.typing ? "typing-indicator" : ""
              }}`}
            >
              {message.file && (
                <>
                  <div className="flex flex-row items-center">
                    {validImageTypes.includes(message.file.type) ? (
                      <img
                        src={URL.createObjectURL(message.file)}
                        alt="Selected"
                        className=" w-auto h-auto max-h-[75px] rounded-[5px]"
                      />
                    ) : (
                      <LuFileText
                        color={"#000000"}
                        size={
                          isScreenMdOrLarger
                            ? moderateScale(25, 1)
                            : moderateScale(20, 1)
                        }
                      />
                    )}
                    <p className="ml-[6px]">{message.file.name}</p>
                  </div>
                  <div
                    className="h-[1px] w-full my-[5px]"
                    style={{
                      backgroundColor:
                        appearance?.userChatTextColor || "#FCFCFC",
                    }}
                  />
                </>
              )}

              <span>{message.content}</span>
            </div>
            <div7
              style={{
                borderWidth: "2px",
                borderColor:
                  appearance?.userChatBubbleBackgroundColor || "#26A69A",
                borderStyle: "solid",

                height: isScreenMdOrLarger
                  ? moderateScale(42, appearance?.chatFontSize || 1)
                  : moderateScale(30, appearance?.chatFontSize || 1),
                width: isScreenMdOrLarger
                  ? moderateScale(42, appearance?.chatFontSize || 1)
                  : moderateScale(30, appearance?.chatFontSize || 1),
                borderRadius: isScreenMdOrLarger
                  ? moderateScale(21, appearance?.chatFontSize || 1)
                  : moderateScale(15, appearance?.chatFontSize || 1),
                padding: moderateScale(5, appearance?.chatFontSize || 1),
              }}
              className="flex shrink-0 justify-center items-center shadow-xl ml-[10px]"
            >
              <BiSolidUser
                color={appearance?.userChatBubbleBackgroundColor || "#26A69A"}
                className="h-full w-full"
              />
            </div7>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatMessage;
